/**
 * Proxy Provider for free grammar checking service
 */

import { BaseProvider } from './BaseProvider'
import type { CheckOptions, ProviderType } from './types'
import type { GrammarError } from '$lib/types/grammar'
import { GrammarErrorFactory } from '../GrammarErrorFactory'
import {
  buildSubscriptionHeaders,
  getSubscriptionRequestMetadata
} from '../subscription/requestMetadata'

export class ProxyProvider extends BaseProvider {
  private proxyUrl: string

  constructor() {
    super({ type: 'proxy' })
    this.proxyUrl = import.meta.env.VITE_PROXY_URL || 'https://appstore.junlim.org'
  }

  getName(): string {
    return '무료 프록시 서버'
  }

  getDescription(): string {
    return '기본 제공되는 무료 한국어 맞춤법 검사 서비스입니다. 일일 사용 제한이 있습니다.'
  }

  requiresApiKey(): boolean {
    return false
  }

  requiresServerUrl(): boolean {
    return false
  }

  getType(): ProviderType {
    return 'proxy'
  }

  async checkText(text: string, options?: CheckOptions): Promise<GrammarError[]> {
    if (!text || text.trim().length === 0) {
      return []
    }

    try {
      const requestMetadata = await getSubscriptionRequestMetadata()
      const response = await this.makeRequest(
        `${this.proxyUrl}/api/grammar/check`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...buildSubscriptionHeaders(requestMetadata)
          },
          body: JSON.stringify({
            text,
            deviceId: requestMetadata.deviceId,
            tier: requestMetadata.tier,
            licenseKey: requestMetadata.licenseKey || undefined
          })
        },
        options?.signal
      )

      if (!response.ok) {
        throw new Error(`Proxy server error: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()

      // Convert proxy response to GrammarError format
      if (data.errors && Array.isArray(data.errors)) {
        
        // Remove duplicates and overlapping errors before mapping
        const uniqueErrors: any[] = []
        
        // Sort errors by position to process them in order
        const sortedErrors = [...data.errors].sort((a, b) => {
          const aStart = a.startPos ?? 0
          const bStart = b.startPos ?? 0
          return aStart - bStart
        })
        
        for (const error of sortedErrors) {
          const start = error.startPos ?? 0
          const end = error.endPos ?? 0
          const orgStr = error.orgStr || ''
          const candWord = Array.isArray(error.candWord) ? error.candWord[0] : error.candWord
          
          // Skip errors where the suggestion is the same as original (no actual error)
          if (orgStr === candWord) {
            continue
          }
          
          // Check if this error overlaps with any existing error
          let shouldAdd = true
          for (let i = 0; i < uniqueErrors.length; i++) {
            const existing = uniqueErrors[i]
            const existingStart = existing.startPos ?? 0
            const existingEnd = existing.endPos ?? 0
            const existingOrgStr = existing.orgStr || ''
            
            // Check for position overlap or containment
            const overlaps = (start <= existingEnd && end >= existingStart) ||
                            (start >= existingStart && start <= existingEnd) ||
                            (end >= existingStart && end <= existingEnd)
            
            if (overlaps) {
              // Keep the one with larger range (more complete error)
              const errorRange = end === start ? 0 : end - start
              const existingRange = existingEnd === existingStart ? 0 : existingEnd - existingStart
              
              if (errorRange > existingRange) {
                // Replace existing with current
                uniqueErrors[i] = error
              }
              shouldAdd = false
              break
            }
          }
          
          if (shouldAdd) {
            uniqueErrors.push(error)
          }
        }
        
        
        return uniqueErrors.map((error: any, index: number) => {
          // Use GrammarErrorFactory if the error format matches bareun
          if (error.errorCode || error.candWord) {
            return GrammarErrorFactory.fromBareunApiError(error, index)
          }
          
          // Otherwise, try to map directly
          return this.mapProxyError(error, index)
        })
      }

      return []
    } catch (error) {
      console.error('Proxy provider error:', error)
      throw new Error(
        error instanceof Error 
          ? `프록시 서버 연결 실패: ${error.message}`
          : '프록시 서버 연결 실패'
      )
    }
  }

  /**
   * Map proxy error format to GrammarError
   */
  private mapProxyError(error: any, index: number): GrammarError {
    return {
      id: this.generateId(),
      type: error.type || 'spelling',
      level: error.level || 'error',
      original: error.original || error.originalText || '',
      originalText: error.original || error.originalText || '',
      position: {
        start: error.position?.start || error.startPos || 0,
        end: error.position?.end || error.endPos || 0
      },
      corrections: this.mapCorrections(error),
      message: error.description || error.help || '맞춤법 오류',
      description: error.description || error.help || '맞춤법 오류',
      aiComment: error.aiComment,
      userAction: error.userAction,
      isOriginalKept: error.isOriginalKept
    }
  }

  /**
   * Map corrections from various formats
   */
  private mapCorrections(error: any): Array<{ id: string; text: string }> {
    // Check for various correction formats
    if (error.corrections && Array.isArray(error.corrections)) {
      return error.corrections.map((corr: any) => ({
        id: this.generateId(),
        text: typeof corr === 'string' ? corr : corr.text
      }))
    }

    if (error.candWord && Array.isArray(error.candWord)) {
      return error.candWord.map((word: string) => ({
        id: this.generateId(),
        text: word
      }))
    }

    if (error.corrected) {
      return [{
        id: this.generateId(),
        text: error.corrected
      }]
    }

    return []
  }
}
